import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { reactivateAccount } from '@/services/privacyRightsService';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const PendingDeletionReactivationBanner: React.FC = () => {
  const { currentUser, refreshCurrentUser } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!currentUser) return null;

  const isPendingDeletion =
    currentUser.status === 'pending_deletion' ||
    currentUser.status === 'pendiente_baja';

  if (!isPendingDeletion || dismissed) return null;

  const effectiveDate = currentUser.deletionEffectiveAt
    ? new Date(currentUser.deletionEffectiveAt).toLocaleDateString('es-ES')
    : 'en 30 días';

  const handleConfirmReactivate = async () => {
    setBusy(true);
    try {
      await reactivateAccount(currentUser);
      setConfirmOpen(false);
      setDismissed(true);
      await refreshCurrentUser();
      toast({
        title: 'Cuenta reactivada con éxito',
        description: 'Se ha cancelado la solicitud de baja y tu cuenta vuelve a estar plenamente activa.',
        duration: 5000,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo reactivar la cuenta.';
      toast({
        title: 'Error al reactivar cuenta',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="w-full bg-amber-500 text-amber-950 dark:bg-amber-600 dark:text-amber-50 py-2.5 px-4 shadow-md sticky top-0 lg:top-0 z-30 mt-14 lg:mt-0">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs sm:text-sm">
          <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-900 dark:text-amber-100 mt-0.5 sm:mt-0" />
            <span className="min-w-0 flex-1 leading-snug">
              <strong>Tu cuenta está en periodo de baja.</strong> La eliminación definitiva se ejecutará el{' '}
              <strong>{effectiveDate}</strong>. Puedes reactivarla antes de esa fecha para conservar tu acceso.
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            className="shrink-0 w-full sm:w-auto bg-white text-amber-900 hover:bg-amber-50 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900 border border-amber-300 font-semibold"
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reactivar mi cuenta
          </Button>
        </div>
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!busy) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <RotateCcw className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>¿Reactivar tu cuenta de Comuni Home?</span>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-xs sm:text-sm text-muted-foreground pt-1">
                <p>
                  Vas a cancelar la solicitud de baja de tu cuenta asociada a{' '}
                  <strong className="text-foreground">{currentUser.email}</strong>.
                </p>
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 p-3 text-xs text-amber-950 dark:text-amber-200 space-y-1.5 leading-relaxed">
                  <p className="font-semibold flex items-center gap-1.5 text-amber-900 dark:text-amber-300">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    Consecuencias de la reactivación:
                  </p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>
                      Se cancelará el periodo de baja y la eliminación definitiva programada para el{' '}
                      <strong>{effectiveDate}</strong>.
                    </li>
                    <li>Tu cuenta recuperará el acceso ordinario a la plataforma con todos tus roles y viviendas.</li>
                    <li>Volverás a recibir las convocatorias y avisos oficiales de tu comunidad por la aplicación.</li>
                  </ul>
                </div>
                <p className="text-xs">
                  Si más adelante deseas volver a tramitar la baja, podrás hacerlo en cualquier momento desde tu perfil.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmReactivate();
              }}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-700 font-semibold"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Reactivando cuenta...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Confirmar y reactivar
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
